import { useQuery } from '@tanstack/react-query';
import type { ApiResponse, ListPlansResponseData } from '@fluenceur/types';
import axiosInstance from '@/libs/axiosInstance';

interface UsePlansQueryOptions {
  enabled?: boolean;
  /**
   * The currency to price the catalogue in, normally the brand's shop currency.
   *
   * Omitted by the public pricing page, which has no brand to read one from and
   * takes the API's fallback. Passing it is what stops an authenticated brand
   * from being quoted euros on screen while being charged in its own currency.
   */
  currency?: string;
}

/**
 * Fetch available subscription plans
 * @param options - Query options including enabled flag and the pricing currency
 * @returns Query result with plans data
 */
export const usePlansQuery = (options: UsePlansQueryOptions = {}) => {
  const { enabled = true, currency } = options;

  return useQuery({
    // The currency is part of the key: two brands on the same browser session,
    // or one brand that reconnects a store in another currency, must not read
    // each other's prices out of the cache.
    queryKey: ['plans', currency ?? null],
    queryFn: async (): Promise<ListPlansResponseData> => {
      const response = await axiosInstance.get<ApiResponse<ListPlansResponseData>>('/plans', {
        params: currency ? { currency } : undefined,
      });
      return response.data.data!;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - plans rarely change
    gcTime: 60 * 60 * 1000, // 1 hour cache
    enabled,
  });
};
